import { usePreferencesStore } from '../store/preferencesStore';

/**
 * Settings → Requests (timeout, follow redirects, HTTP version) and
 * Settings → Editor → Max response size, in the shape `send_http_request`
 * takes them (`src-tauri/src/request_preferences.rs`).
 *
 * Until 24.09.2026 these preferences were saved and never read. They are now
 * passed as `sendRequest(withUserNetwork({ ... }))` by the
 * paths that send the USER's own requests — the request builder, the
 * collection runner, `pm.sendRequest` and the AI's "send this request" —
 * and by nothing else: an AI provider call or a link preview is not a request
 * the user configured, and keeps its own budget.
 *
 * SSL verification is NOT here. It has exactly one source of truth, the
 * certificate store's `verifySSL` (Settings → Certificates, mirrored on
 * Settings → Requests), and `sendHttpRequest` already reads it.
 *
 * Values are re-validated here rather than trusted from the store, because a
 * hand-edited preferences file is the other way in.
 */
// 0 = no limit, the default since the 24.09.2026 review: the setting never
// took effect before, so a default limit would start failing responses that
// work today. Stored under a NEW key (`responseSizeLimitMb`); the old
// `maxResponseSizeMb` (default 50, never honoured) is dropped, not migrated.
export const RESPONSE_SIZE_LIMIT_MB_DEFAULT = 0;
const BYTES_PER_MB = 1024 * 1024;
const HTTP_VERSIONS = ['auto', '1.1', '2'];

export function requestNetworkOptions(prefs = usePreferencesStore.getState()) {
  const timeout = Number(prefs?.requestTimeout);
  const sizeMb = Number(prefs?.responseSizeLimitMb);
  return {
    timeout_ms: Number.isFinite(timeout) ? Math.max(0, Math.floor(timeout)) : 30000,
    follow_redirects: prefs?.followRedirects !== false,
    http_version: HTTP_VERSIONS.includes(prefs?.httpVersion) ? prefs.httpVersion : 'auto',
    // 0 = no limit (sent as 0: Rust reads 0 as none, and the field's
    // presence is part of what marks this as a user request).
    max_response_bytes:
      (Number.isFinite(sizeMb) ? Math.min(500, Math.max(0, Math.floor(sizeMb))) : RESPONSE_SIZE_LIMIT_MB_DEFAULT)
      * BYTES_PER_MB,
  };
}

/**
 * Mark a request as the USER's own: attach the Settings → Requests values.
 * This is what every user-request call site wraps its request in, and the
 * mark is what lets "SSL verification: off" apply to it (and to nothing else).
 */
export function withUserNetwork(requestData, prefs) {
  return { ...requestData, network: requestNetworkOptions(prefs) };
}
